import { useMemo } from 'react';
import type { EChartsType } from 'echarts/core';

import type { TimeSeriesChartProps } from '@/shared/charts/time-series-chart/TimeSeriesChart.types';
import type { TimeSeriesChartTheme } from '@/shared/charts/time-series-chart/theme/TimeSeriesChartTheme.types';

import { type BuildChartOptionParams, buildChartOption } from '../buildChartOption';
import type { LineViewport } from '../types/LineViewport.types';
import { usePlotWidth } from './usePlotWidth';
import { useVisibleRange } from './useVisibleRange';

type UseTimeSeriesChartOptionParams = Pick<
  TimeSeriesChartProps,
  | 'series'
  | 'rangeTrack'
  | 'timeDomain'
  | 'highlight'
  | 'navigation'
  | 'isTooltipEnabled'
  | 'sectionLabels'
  | 'valueAxisScales'
  | 'locale'
> & {
  chart: EChartsType | undefined;
  theme: TimeSeriesChartTheme;
  renderTooltipContent: BuildChartOptionParams['renderTooltipContent'];
};

/**
 * The ECharts option for the current props and viewport. The viewport — the settled visible window
 * and the plot width — is the only renderer-owned input; everything else comes from props.
 *
 * Memoised on purpose: building the lines walks the visible window of every series. Consumers keep
 * their series, range track, axis scales and behaviour objects referentially stable while their contents are
 * unchanged, so the option is rebuilt only when something it depends on actually changed.
 *
 * Attach the returned ref to the element that sizes the chart.
 */
export function useTimeSeriesChartOption({
  chart,
  series,
  rangeTrack,
  timeDomain,
  highlight,
  navigation,
  isTooltipEnabled,
  sectionLabels,
  valueAxisScales,
  locale,
  theme,
  renderTooltipContent,
}: UseTimeSeriesChartOptionParams) {
  const visibleRange = useVisibleRange({ chart, timeDomain });
  const { containerRef, plotWidthPx } = usePlotWidth();

  const viewport = useMemo<LineViewport>(() => ({ visibleRange, plotWidthPx }), [visibleRange, plotWidthPx]);

  const option = useMemo(
    () =>
      buildChartOption({
        series,
        rangeTrack,
        timeDomain,
        highlight,
        navigation,
        isTooltipEnabled,
        sectionLabels,
        valueAxisScales,
        locale,
        theme,
        renderTooltipContent,
        viewport,
      }),
    [
      series,
      rangeTrack,
      timeDomain,
      highlight,
      navigation,
      isTooltipEnabled,
      sectionLabels,
      valueAxisScales,
      locale,
      theme,
      renderTooltipContent,
      viewport,
    ]
  );

  return { option, containerRef };
}
